import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import { ProjectBasedLearningResponseDto } from "@packages/api/madlenAPI.schemas";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

interface ProjectBasedLearningCardProps {
  project: ProjectBasedLearningResponseDto;
  customizationSettings: PdfCustomizationSettings;
}

const styles = StyleSheet.create({
  projectContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#ffffff",
  },
  section: {
    marginBottom: 12,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  // Project Problem section styles
  problemHeader: {
    backgroundColor: "#dbeafe",
  },
  problemTitle: {
    color: "#2563eb",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Real Life Connection section styles
  connectionHeader: {
    backgroundColor: "#dcfce7",
  },
  connectionTitle: {
    color: "#16a34a",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Process Steps section styles
  stepsHeader: {
    backgroundColor: "#f3e8ff",
  },
  stepsTitle: {
    color: "#9333ea",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Feedback Points section styles
  feedbackHeader: {
    backgroundColor: "#fef3c7",
  },
  feedbackTitle: {
    color: "#d97706",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Assessment section styles
  assessmentHeader: {
    backgroundColor: "#fee2e2",
  },
  assessmentTitle: {
    color: "#dc2626",
    fontSize: 11,
    fontWeight: "bold",
  },
  // Peer/Self Assessment section styles
  peerSelfHeader: {
    backgroundColor: "#ede9fe",
  },
  peerSelfTitle: {
    color: "#7c3aed",
    fontSize: 11,
    fontWeight: "bold",
  },
  listItem: {
    marginBottom: 4,
    paddingLeft: 12,
  },
});

const ProjectBasedLearningDocument = ({
  project,
  customizationSettings,
}: ProjectBasedLearningCardProps) => {
  const { t } = useTranslation();
  const markdownStyle = {
    text: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
    listItem: {
      text: {
        ...commonStyles.sectionContent,
        fontSize: scaleFontSize(
          commonStyles.sectionContent.fontSize,
          customizationSettings?.fontSize,
        ),
      },
    },
  };
  return (
    <PdfLayout
      courseName={project.courseName || ""}
      gradeLevel={project.gradeLevel || 0}
      topic={project.title || project.prompt || ""}
      imageUrl={project.imageUrl}
      headerTextColor="#2563eb"
      customizationSettings={customizationSettings}
      author={project.authorName || ""}
      createdDate={project.createdAt || ""}
    >
      <View style={styles.projectContainer}>
        {/* Project Problem Section */}
        <View style={styles.section}>
          <View style={[styles.sectionHeader, styles.problemHeader]}>
            <Text
              style={[
                styles.problemTitle,
                {
                  fontSize: scaleFontSize(
                    styles.problemTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
              ]}
            >
              {t("projectBasedLearning.projectProblem")}
            </Text>
          </View>
          {(project.projectProblem?.questions || []).map((question, index) => (
            <View key={index} style={styles.listItem}>
              <MarkdownRenderer
                content={question}
                customStyle={markdownStyle}
              />
            </View>
          ))}
        </View>

        {/* Real Life Connection Section */}
        <View style={styles.section} wrap={false}>
          <View style={[styles.sectionHeader, styles.problemHeader]}>
            <Text
              style={[
                styles.problemTitle,
                {
                  fontSize: scaleFontSize(
                    styles.problemTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
              ]}
            >
              {t("projectBasedLearning.realLifeConnection")}
            </Text>
          </View>
          <View
            style={{
              paddingLeft: 12,
            }}
          >
            <MarkdownRenderer
              content={project.realLifeConnection || ""}
              customStyle={markdownStyle}
            />
          </View>
        </View>

        {/* Process Steps Section */}
        <View style={styles.section} wrap={false}>
          <View style={[styles.sectionHeader, styles.stepsHeader]}>
            <Text
              style={[
                styles.stepsTitle,
                {
                  fontSize: scaleFontSize(
                    styles.stepsTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
              ]}
            >
              {t("projectBasedLearning.processSteps")}
            </Text>
          </View>
          {(project.processSteps || []).map((step, index) => (
            <View key={index} style={styles.listItem}>
              <MarkdownRenderer content={step} customStyle={markdownStyle} />
            </View>
          ))}
        </View>

        {/* Feedback Points Section */}
        <View style={styles.section} wrap={false}>
          <View style={[styles.sectionHeader, styles.feedbackHeader]}>
            <Text
              style={[
                styles.feedbackTitle,
                {
                  fontSize: scaleFontSize(
                    styles.feedbackTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
              ]}
            >
              {t("projectBasedLearning.feedbackPoints")}
            </Text>
          </View>
          {(project.feedbackPoints || []).map((point, index) => (
            <View key={index} style={styles.listItem}>
              <MarkdownRenderer content={point} customStyle={markdownStyle} />
            </View>
          ))}
        </View>

        {/* Assessment Section */}
        {(project.assessment || project.assessment) && (
          <View style={styles.section} wrap={false}>
            <View style={[styles.sectionHeader, styles.assessmentHeader]}>
              <Text
                style={[
                  styles.assessmentTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.assessmentTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("projectBasedLearning.assessment")}
              </Text>
            </View>
            {/* Criteria */}
            <View style={styles.listItem}>
              <Text
                style={[
                  commonStyles.sectionContent,
                  {
                    fontWeight: "bold",
                    fontSize: scaleFontSize(
                      commonStyles.sectionContent.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("projectBasedLearning.criteria")}:
              </Text>
              {(project.assessment?.criteria || []).map((criterion, index) => (
                <MarkdownRenderer
                  key={index}
                  content={`- ${criterion}`}
                  customStyle={markdownStyle}
                />
              ))}
            </View>
          </View>
        )}

        {/* Peer/Self Assessment Section */}
        {project.peerSelfAssessment && (
          <View style={styles.section} wrap={false}>
            <View style={[styles.sectionHeader, styles.peerSelfHeader]}>
              <Text
                style={[
                  styles.peerSelfTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.peerSelfTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("projectBasedLearning.peerSelfAssessment")}
              </Text>
            </View>
            {/* Peer Review Questions */}
            <View style={styles.listItem}>
              <Text
                style={[
                  commonStyles.sectionContent,
                  {
                    fontWeight: "bold",
                    fontSize: scaleFontSize(
                      commonStyles.sectionContent.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("projectBasedLearning.peerReviewQuestions")}:
              </Text>
              {(project.peerSelfAssessment?.peer_review_questions || []).map(
                (question, index) => (
                  <MarkdownRenderer
                    key={index}
                    content={`- ${question}`}
                    customStyle={markdownStyle}
                  />
                ),
              )}
            </View>
            {/* Self Reflection Questions */}
            <View style={styles.listItem}>
              <Text
                style={[
                  commonStyles.sectionContent,
                  {
                    fontWeight: "bold",
                    fontSize: scaleFontSize(
                      commonStyles.sectionContent.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("projectBasedLearning.selfReflectionQuestions")}:
              </Text>
              {(
                project.peerSelfAssessment?.self_reflection_questions || []
              ).map((question, index) => (
                <MarkdownRenderer
                  key={index}
                  content={`- ${question}`}
                  customStyle={markdownStyle}
                />
              ))}
            </View>
          </View>
        )}
      </View>
    </PdfLayout>
  );
};

function ProjectBasedLearningCardPDF(props: ProjectBasedLearningCardProps) {
  return <ProjectBasedLearningDocument {...props} />;
}

export default ProjectBasedLearningCardPDF;
